/**
 * SDKSetupWizard Component
 *
 * Setup wizard for using LeftShield SDKs (Node.js, Python, .NET).
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
  Tab,
  TabList,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '650px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
});

interface SDKSetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

type SDKTab = 'nodejs' | 'python' | 'dotnet';

export const SDKSetupWizard: React.FC<SDKSetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SDKTab>('nodejs');

  const copyToClipboard = (text: string, step: string) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const nodeInstall = 'npm install @leftshield/sdk';
  const nodeExample = `import { LeftShieldClient } from '@leftshield/sdk';

const client = new LeftShieldClient({
  apiKey: '${apiKey}',
});

// Validate code
const result = await client.validateCode({
  code: 'const secret = "api-key-123";',
  language: 'javascript',
});

console.log(result.decision); // 'allow' | 'warn' | 'block'
console.log(result.violations);

// Batch validation
const batchResult = await client.validateBatch({
  files: [
    { path: 'src/app.js', content: '...' },
    { path: 'src/utils.js', content: '...' },
  ],
});`;

  const pythonInstall = 'pip install leftshield';
  const pythonExample = `from leftshield import LeftShieldClient

client = LeftShieldClient(api_key="${apiKey}")

# Validate code
result = client.validate_code(
    code='secret = "api-key-123"',
    language="python"
)

print(result.decision)  # 'allow' | 'warn' | 'block'
print(result.violations)

# Async support
async with LeftShieldClient(api_key="${apiKey}") as client:
    result = await client.validate_code_async(code="...")`;

  const dotnetInstall = 'dotnet add package LeftShield.Sdk';
  const dotnetExample = `using LeftShield.Sdk;

var client = new LeftShieldClient("${apiKey}");

// Validate code
var result = await client.ValidateCodeAsync(new CodeValidationRequest
{
    Code = "var secret = \\"api-key-123\\";",
    Language = "csharp"
});

Console.WriteLine(result.Decision); // Allow, Warn, Block
Console.WriteLine(result.Violations.Count);

// Scan code for security issues
var scanResult = await client.ScanCodeAsync(new CodeScanRequest
{
    Code = code,
    ScanTypes = new[] { "sast", "secrets" }
});`;

  const renderContent = () => {
    switch (activeTab) {
      case 'nodejs':
        return (
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <Text weight="semibold">Installation</Text>
              <div className={styles.codeBlock}>
                <code>{nodeInstall}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'node-install' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(nodeInstall, 'node-install')}
                />
              </div>
            </div>
            <div className={styles.section}>
              <Text weight="semibold">Usage</Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{nodeExample}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'node-example' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(nodeExample, 'node-example')}
                />
              </div>
            </div>
            <Link href="https://www.npmjs.com/package/@leftshield/sdk" target="_blank">
              View on npm
            </Link>
          </div>
        );

      case 'python':
        return (
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <Text weight="semibold">Installation</Text>
              <div className={styles.codeBlock}>
                <code>{pythonInstall}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'python-install' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(pythonInstall, 'python-install')}
                />
              </div>
            </div>
            <div className={styles.section}>
              <Text weight="semibold">Usage</Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{pythonExample}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'python-example' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(pythonExample, 'python-example')}
                />
              </div>
            </div>
            <Link href="https://pypi.org/project/leftshield/" target="_blank">
              View on PyPI
            </Link>
          </div>
        );

      case 'dotnet':
        return (
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <Text weight="semibold">Installation</Text>
              <div className={styles.codeBlock}>
                <code>{dotnetInstall}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'dotnet-install' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(dotnetInstall, 'dotnet-install')}
                />
              </div>
            </div>
            <div className={styles.section}>
              <Text weight="semibold">Usage</Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{dotnetExample}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 'dotnet-example' ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(dotnetExample, 'dotnet-example')}
                />
              </div>
            </div>
            <Link href="https://www.nuget.org/packages/LeftShield.Sdk" target="_blank">
              View on NuGet
            </Link>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>SDK Documentation</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                Use the LeftShield SDK to integrate code validation into your applications and scripts.
              </MessageBarBody>
            </MessageBar>

            <TabList
              selectedValue={activeTab}
              onTabSelect={(_, data) => setActiveTab(data.value as SDKTab)}
            >
              <Tab value="nodejs">Node.js</Tab>
              <Tab value="python">Python</Tab>
              <Tab value="dotnet">.NET</Tab>
            </TabList>

            {renderContent()}

            <Divider />

            <MessageBar intent="success">
              <MessageBarBody>
                All SDKs include automatic retry, rate limit handling, and type safety.{' '}
                <Link href="https://docs.leftshield.io/sdk" target="_blank">
                  View full documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
